import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Nav from '../shared/Nav';
import DateOfBirthPicker from '../shared/DateOfBirthPicker';
import { getQuestionsForRole, sampleStratifiedByTier } from '../../data/questions';
import {
  createApplicant,
  markVideoIntroWatched,
  markStage1Started,
  markStage1Done,
} from '../../lib/supabase';
import './ApplicationForm.css';

const DIAG_SECONDS_PER_QUESTION = 50; // tiempo asignado por pregunta
const FORM_STORAGE_KEY = 'impekble_application_draft';

// QA dev tools — solo aparecen cuando REACT_APP_SHOW_DEV_TOOLS=true.
// Marca la respuesta correcta y permite anotar comentarios por pregunta.
const SHOW_DEV_TOOLS = process.env.REACT_APP_SHOW_DEV_TOOLS === 'true';

// Botón para saltar el video. Activo cuando SHOW_DEV_TOOLS=true (todo el modo
// dev en local) o cuando se setea explícitamente REACT_APP_ALLOW_SKIP_VIDEO=true
// (caso staging Vercel: queremos el skip pero sin el panel QA).
const ALLOW_SKIP_VIDEO =
  SHOW_DEV_TOOLS || process.env.REACT_APP_ALLOW_SKIP_VIDEO === 'true';
const QA_COMMENTS_KEY = 'impekble_qa_comments';
const QA_CHANGE_TYPES = [
  { value: 'texto', label: 'Texto', emoji: '📝' },
  { value: 'foto', label: 'Foto', emoji: '📷' },
  { value: 'ambos', label: 'Ambos', emoji: '🔀' },
];

function formatChileanPhone(value) {
  let digits = value.replace(/\D/g, '');
  if (digits.startsWith('56')) digits = digits.slice(2);
  digits = digits.slice(0, 9);
  if (!digits) return '';
  let result = '+56';
  if (digits.length >= 1) result += ' ' + digits[0];
  if (digits.length >= 2) result += ' ' + digits.slice(1, Math.min(5, digits.length));
  if (digits.length >= 6) result += ' ' + digits.slice(5, 9);
  return result;
}

export default function ApplicationForm() {
  const [searchParams] = useSearchParams();
  const preRole = searchParams.get('role') || null;

  // ── Estado general ──
  const [step, setStep] = useState(1); // 1=form, 2=diag, 3=done

  // ── Formulario ──
  const [form, setForm] = useState(() => {
    const defaults = {
      name: '',
      lastName: '',
      nationalId: '',
      phoneNumber: '',
      email: '',
      birthDate: '',
      nationality: '',
      locationAddress: '',
      recruitmentRole: preRole || '',
      aboutMe: '',
    };
    try {
      const saved = localStorage.getItem(FORM_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return { ...defaults, ...parsed, recruitmentRole: preRole || parsed.recruitmentRole || '' };
      }
    } catch (_) {
      // localStorage not available or invalid JSON — fall through to defaults
    }
    return defaults;
  });
  const [formErrors, setFormErrors] = useState({});

  // Persistir borrador mientras el postulante llena el formulario
  useEffect(() => {
    if (step !== 1) return;
    try {
      localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(form));
    } catch (_) {
      // ignore — localStorage may be disabled (private mode)
    }
  }, [form, step]);

  // ── Diagnóstico ──
  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [timeLimit, setTimeLimit] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [diagStarted, setDiagStarted] = useState(false);
  const [introWatched, setIntroWatched] = useState(false);
  const [introPlaying, setIntroPlaying] = useState(false);
  const introVideoRef = useRef(null);

  const playIntro = () => {
    const v = introVideoRef.current;
    if (!v) return;
    v.play().then(() => setIntroPlaying(true)).catch(() => { /* user gesture required, ignore */ });
  };

  const handleIntroEnded = () => {
    setIntroPlaying(false);
    setIntroWatched(true);
    // Persistir en Supabase (no bloquea la UI)
    markVideoIntroWatched();
  };

  // ── Resultado ──
  const [diagResult, setDiagResult] = useState(null);

  // ── QA dev: comentarios sobre preguntas (solo local/staging) ──
  const [qaComments, setQaComments] = useState(() => {
    if (!SHOW_DEV_TOOLS) return {};
    try {
      const raw = localStorage.getItem(QA_COMMENTS_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (_) {
      return {};
    }
  });
  const [qaDraft, setQaDraft] = useState('');
  const [qaChangeType, setQaChangeType] = useState('texto');
  const [qaPanelOpen, setQaPanelOpen] = useState(false);
  const [qaSaving, setQaSaving] = useState(false);

  useEffect(() => {
    if (!SHOW_DEV_TOOLS) return;
    try {
      localStorage.setItem(QA_COMMENTS_KEY, JSON.stringify(qaComments));
    } catch (_) { /* localStorage may be disabled */ }
  }, [qaComments]);

  // Carga el comentario guardado de la pregunta actual cuando cambia
  // (qaComments NO va en deps a propósito: cada save lo regrabaría sobre el draft)
  const qaCommentsRef = useRef(qaComments);
  useEffect(() => { qaCommentsRef.current = qaComments; }, [qaComments]);
  useEffect(() => {
    if (!SHOW_DEV_TOOLS) return;
    const q = questions[currentQ];
    const saved = q ? qaCommentsRef.current[q.id] : null;
    setQaDraft(saved ? saved.comment : '');
    setQaChangeType(saved && saved.changeType ? saved.changeType : 'texto');
    setQaSaving(false);
  }, [currentQ, questions]);

  // Auto-guardado del comentario mientras se escribe (debounce 400ms).
  // Así el usuario nunca pierde lo que escribió aunque se caiga el dev server.
  useEffect(() => {
    if (!SHOW_DEV_TOOLS) return;
    const q = questions[currentQ];
    if (!q) return;
    const trimmed = qaDraft.trim();
    const existing = qaComments[q.id] || {};
    const sameText = trimmed === (existing.comment || '');
    const sameType = qaChangeType === (existing.changeType || 'texto');
    // Si no hay texto, lo borramos. Si hay texto y cambió algo, lo guardamos.
    if (!trimmed && !existing.comment) {
      setQaSaving(false);
      return;
    }
    if (sameText && sameType) {
      setQaSaving(false);
      return;
    }
    setQaSaving(true);
    const t = setTimeout(() => {
      setQaComments((prev) => {
        if (!trimmed) {
          const { [q.id]: _omit, ...rest } = prev;
          return rest;
        }
        return {
          ...prev,
          [q.id]: {
            questionText: q.question,
            image: q.image || null,
            correctIndex: q.correct,
            options: q.options,
            comment: trimmed,
            changeType: qaChangeType,
            updatedAt: new Date().toISOString(),
          },
        };
      });
      setQaSaving(false);
    }, 400);
    return () => clearTimeout(t);
  }, [qaDraft, qaChangeType, currentQ, questions, qaComments]);

  const removeQaComment = (id) => {
    setQaComments((prev) => {
      const { [id]: _omit, ...rest } = prev;
      return rest;
    });
    const q = questions[currentQ];
    if (q && q.id === id) setQaDraft('');
  };

  const exportQaComments = () => {
    const blob = new Blob([JSON.stringify(qaComments, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `qa-comments-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const [qaCopied, setQaCopied] = useState(false);
  const copyQaComments = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(qaComments, null, 2));
      setQaCopied(true);
      setTimeout(() => setQaCopied(false), 1500);
    } catch (_) {
      // si falla clipboard (browser viejo o permisos), caemos al export
      exportQaComments();
    }
  };

  // ── Handlers del formulario ──
  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = 'Requerido';
    if (!form.nationalId.trim()) errors.nationalId = 'Requerido';
    if (!form.phoneNumber.trim()) errors.phoneNumber = 'Requerido';
    if (!form.recruitmentRole) errors.recruitmentRole = 'Selecciona un rol';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const submitForm = async () => {
    if (!validateForm()) return;

    // Limpiar borrador — ya envió el paso 1
    try { localStorage.removeItem(FORM_STORAGE_KEY); } catch (_) {}

    // Persistir en Supabase (crea fila en `recruitment` y guarda id en localStorage).
    // No bloqueamos la UI: si Supabase falla, el flujo sigue.
    createApplicant(form);

    // En producción cada postulante recibe 12 preguntas estratificadas por tier
    // (4 de tier 1, 4 de tier 2, 3 de tier 3, 1 de tier 4). En modo QA review
    // se carga el banco completo del rol para poder anotar todo el set.
    let q = SHOW_DEV_TOOLS
      ? getQuestionsForRole(form.recruitmentRole)
      : sampleStratifiedByTier(form.recruitmentRole);

    // QA mode: sortear con pendientes (sin comentario) primero,
    // ya comentadas al final. Mantiene el orden interno de cada grupo.
    if (SHOW_DEV_TOOLS) {
      const commented = q.filter((qq) => qaComments[qq.id]);
      const pending = q.filter((qq) => !qaComments[qq.id]);
      q = [...pending, ...commented];
    }

    // En QA mode no usamos timer (revisar 150+ preguntas no entra en 25min).
    const limit = SHOW_DEV_TOOLS ? 0 : q.length * DIAG_SECONDS_PER_QUESTION;
    setQuestions(q);
    setTimeLimit(limit);
    setTimeLeft(limit);
    setStep(2);
    window.scrollTo(0, 0);
  };

  // ── Timer del diagnóstico ──
  useEffect(() => {
    if (!timerActive || timeLeft <= 0) return;
    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          finishDiag();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [timerActive, timeLeft]);

  const startDiag = () => {
    setDiagStarted(true);
    setTimerActive(true);
    markStage1Started();
  };

  const selectAnswer = (qIdx, optIdx) => {
    setAnswers((prev) => ({ ...prev, [qIdx]: optIdx }));
  };

  const nextQuestion = () => {
    if (currentQ >= questions.length - 1) {
      finishDiag();
      return;
    }
    setCurrentQ((prev) => prev + 1);
  };

  const finishDiag = useCallback(() => {
    setTimerActive(false);

    // Calcular score
    let correct = 0;
    questions.forEach((q, i) => {
      if (answers[i] === q.correct) correct++;
    });

    const percentage = Math.round((correct / questions.length) * 100);
    setDiagResult({ correct, total: questions.length, percentage });

    // Persistir respuestas y score en Supabase (mapeo id_pregunta → índice respuesta).
    const answersById = {};
    questions.forEach((q, i) => {
      if (answers[i] !== undefined) answersById[q.id] = answers[i];
    });
    markStage1Done({ answers: answersById, score: percentage });

    setStep(3);
    window.scrollTo(0, 0);
  }, [questions, answers]);

  // ── Timer display ──
  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const timerPercent = timeLimit > 0 ? (timeLeft / timeLimit) * 100 : 0;
  const timerClass =
    timerPercent < 15 ? 'ik-timer-bar__fill--danger' :
    timerPercent < 40 ? 'ik-timer-bar__fill--warning' : '';

  // ── Roles ──
  const roles = [
    {
      key: 'limpieza',
      label: 'Operador de limpieza',
      icon: '🧹',
      tagline: 'Prepara propiedades para el huésped',
      color: '#1DE9B6',
      description:
        'Limpias departamentos y casas de arriendo turístico después del check-out. Sigues un checklist por ambiente, tomas fotos antes y después con la app, y dejas todo listo para que el próximo huésped llegue a un lugar impecable.',
      willDo: [
        'Limpieza completa post check-out: baño, cocina, dormitorios y living',
        'Registro de llegada con geolocalización en la app móvil',
        'Fotos de evidencia antes y después de cada servicio',
        'Reporte de objetos olvidados, daños o situaciones especiales',
      ],
      needs: [
        'Experiencia en limpieza profesional o de hotelería',
        'Celular con datos móviles para usar la app en terreno',
        'Disponibilidad para movilizarte entre propiedades',
        'Atención al detalle y capacidad de trabajar con autonomía',
      ],
      footer: 'Trabajo en terreno · Horarios flexibles · Pago por servicio completado',
    },
    {
      key: 'lavado',
      label: 'Operador de lavado',
      icon: '🫧',
      tagline: 'Procesa y prepara la ropa',
      color: '#6366F1',
      description:
        'Recibes ropa sucia de las propiedades, la clasificas, lavas, secas, planchas y armas paquetes según la configuración de cada servicio. Controlas inventario, reportas daños y aseguras que cada paquete salga completo y en estándar.',
      willDo: [
        'Recibir, clasificar y lavar ropa por color, tela y temperatura',
        'Armar paquetes con QR según configuración de la propiedad',
        'Control de inventario: conteo al recibir y al despachar',
        'Reporte de prendas dañadas, faltantes o del huésped',
      ],
      needs: [
        'Experiencia en lavandería profesional u hotelera',
        'Conocimiento de etiquetas de cuidado y temperaturas',
        'Orden y capacidad de trabajar con registros e inventario',
        'Espacio para procesar ropa (bodega o domicilio propio)',
      ],
      footer: 'Trabajo en bodega o domicilio · Turnos programados · Pago por paquete procesado',
    },
    {
      key: 'inspector',
      label: 'Inspector de calidad',
      icon: '🔍',
      tagline: 'Verifica el estándar de limpieza',
      color: '#F59E0B',
      description:
        'Revisas propiedades después de la limpieza para verificar que cumplan el estándar Impekble antes de que llegue el huésped. Fotografías cada ambiente, clasificas hallazgos como observación o error crítico, y generas retrabajos cuando corresponde.',
      willDo: [
        'Inspección post-limpieza con checklist objetivo por ambiente',
        'Fotografía de evidencia: estado general y detalles por zona',
        'Clasificar hallazgos: observación menor vs error crítico',
        'Generar órdenes de retrabajo cuando hay fallas que corregir',
      ],
      needs: [
        'Ojo crítico para el detalle — ver lo que otros no ven',
        'Criterio para distinguir suciedad de daño o mantenimiento',
        'Imparcialidad: evalúas con el mismo estándar siempre',
        'Experiencia en limpieza, hotelería o control de calidad',
      ],
      footer: 'Trabajo en terreno · Inspecciones programadas · Pago por visita realizada',
    },
  ];

  // ── Step dots ──
  const dots = [1, 2, 3].map((s) => {
    let cls = 'ik-dot';
    if (s < step) cls += ' ik-dot--done';
    if (s === step) cls += ' ik-dot--active';
    return <div key={s} className={cls} />;
  });

  const qaCommentEntries = SHOW_DEV_TOOLS ? Object.entries(qaComments) : [];

  return (
    <div className="appform">
      <Nav>
        <div className="ik-dots">{dots}</div>
      </Nav>

      {SHOW_DEV_TOOLS && (
        <div className={`appform__qa-panel ${qaPanelOpen ? 'appform__qa-panel--open' : ''}`}>
          <button
            type="button"
            className="appform__qa-panel-toggle"
            onClick={() => setQaPanelOpen((o) => !o)}
            title="Comentarios QA"
          >
            🛠 {qaCommentEntries.length}
          </button>
          {qaPanelOpen && (
            <div className="appform__qa-panel-body">
              <div className="appform__qa-panel-head">
                <span>QA — {qaCommentEntries.length} comentario{qaCommentEntries.length === 1 ? '' : 's'}</span>
                <div className="appform__qa-panel-head-actions">
                  <button
                    type="button"
                    className="appform__qa-btn appform__qa-btn--export"
                    onClick={copyQaComments}
                    disabled={qaCommentEntries.length === 0}
                  >
                    {qaCopied ? '✓ copiado' : '📋 Copiar'}
                  </button>
                  <button
                    type="button"
                    className="appform__qa-btn appform__qa-btn--export"
                    onClick={exportQaComments}
                    disabled={qaCommentEntries.length === 0}
                  >
                    ⬇ JSON
                  </button>
                </div>
              </div>
              {qaCommentEntries.length === 0 ? (
                <div className="appform__qa-panel-empty">
                  Sin comentarios todavía. Anotá uno desde cualquier pregunta del quiz.
                </div>
              ) : (
                <>
                  <div className="appform__qa-panel-counts">
                    {QA_CHANGE_TYPES.map((t) => {
                      const n = qaCommentEntries.filter(
                        ([, c]) => (c.changeType || 'texto') === t.value
                      ).length;
                      if (n === 0) return null;
                      return (
                        <span
                          key={t.value}
                          className={`appform__qa-type-pill appform__qa-type-pill--${t.value}`}
                        >
                          {t.emoji} {t.label}: {n}
                        </span>
                      );
                    })}
                  </div>
                  <ul className="appform__qa-panel-list">
                    {qaCommentEntries.map(([id, c]) => {
                      const type = c.changeType || 'texto';
                      return (
                        <li key={id} className="appform__qa-panel-item">
                          <div className="appform__qa-panel-item-head">
                            <span className={`appform__qa-type-pill appform__qa-type-pill--${type}`}>
                              {(QA_CHANGE_TYPES.find((t) => t.value === type) || {}).emoji}
                            </span>
                            <code>{id}</code>
                            <button
                              type="button"
                              className="appform__qa-btn appform__qa-btn--delete"
                              onClick={() => removeQaComment(id)}
                            >
                              ×
                            </button>
                          </div>
                          <div className="appform__qa-panel-item-q">{c.questionText}</div>
                          <div className="appform__qa-panel-item-c">{c.comment}</div>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>
      )}

      <div className="appform__body">
        {/* ══ PASO 1: Formulario ══ */}
        {step === 1 && (
          <div className="appform__section ik-fade-in-up">
            <h2 className="ik-section-title">Tu información</h2>
            <p className="ik-section-sub">
              Primero lo básico. Esto toma menos de 2 minutos.
            </p>

            <div className="ik-field-row" style={{ marginTop: 28 }}>
              <div className="ik-field">
                <label className="ik-label">
                  Nombre <span className="ik-label__req">*</span>
                </label>
                <input
                  className={`ik-input ${formErrors.name ? 'ik-input--error' : ''}`}
                  type="text"
                  value={form.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="Tu nombre"
                />
              </div>
              <div className="ik-field">
                <label className="ik-label">Apellido</label>
                <input
                  className="ik-input"
                  type="text"
                  value={form.lastName}
                  onChange={(e) => updateField('lastName', e.target.value)}
                  placeholder="Tu apellido"
                />
              </div>
            </div>

            <div className="ik-field">
              <label className="ik-label">
                RUT / Pasaporte <span className="ik-label__req">*</span>
              </label>
              <input
                className={`ik-input ${formErrors.nationalId ? 'ik-input--error' : ''}`}
                type="text"
                value={form.nationalId}
                onChange={(e) => updateField('nationalId', e.target.value)}
                placeholder="12.345.678-9"
              />
            </div>

            <div className="ik-field-row">
              <div className="ik-field">
                <label className="ik-label">Fecha de nacimiento</label>
                <DateOfBirthPicker
                  value={form.birthDate}
                  onChange={(iso) => updateField('birthDate', iso)}
                />
              </div>
              <div className="ik-field">
                <label className="ik-label">
                  Teléfono / WhatsApp <span className="ik-label__req">*</span>
                </label>
                <input
                  className={`ik-input ${formErrors.phoneNumber ? 'ik-input--error' : ''}`}
                  type="tel"
                  inputMode="tel"
                  value={form.phoneNumber}
                  onChange={(e) => updateField('phoneNumber', formatChileanPhone(e.target.value))}
                  placeholder="+56 9 1234 5678"
                />
              </div>
            </div>

            <div className="ik-field">
              <label className="ik-label">Correo electrónico</label>
              <input
                className="ik-input"
                type="email"
                value={form.email}
                onChange={(e) => updateField('email', e.target.value)}
                placeholder="tu@correo.cl"
              />
            </div>

            <div className="ik-field">
              <label className="ik-label">Dirección / Comuna</label>
              <input
                className="ik-input"
                type="text"
                value={form.locationAddress}
                onChange={(e) => updateField('locationAddress', e.target.value)}
                placeholder="Ej: Providencia, Santiago"
              />
            </div>

            <div className="ik-field">
              <label className="ik-label">
                ¿A qué rol postulas? <span className="ik-label__req">*</span>
              </label>
              <div className="appform__roles">
                {roles.map((r) => {
                  const isSelected = form.recruitmentRole === r.key;
                  return (
                    <button
                      key={r.key}
                      type="button"
                      className={`appform__role-card ${isSelected ? 'appform__role-card--selected' : ''}`}
                      style={isSelected ? { borderColor: r.color } : undefined}
                      onClick={() =>
                        updateField('recruitmentRole', isSelected ? '' : r.key)
                      }
                      aria-pressed={isSelected}
                    >
                      <div className="appform__role-icon">{r.icon}</div>
                      <div className="appform__role-name">{r.label}</div>
                      <div
                        className="appform__role-tagline"
                        style={isSelected ? { color: r.color } : undefined}
                      >
                        {r.tagline}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Panel expandido del rol seleccionado */}
              {(() => {
                const sel = roles.find((r) => r.key === form.recruitmentRole);
                if (!sel) return null;
                return (
                  <div
                    className="appform__role-detail"
                    style={{ '--role-color': sel.color }}
                  >
                    <p className="appform__role-detail-desc">{sel.description}</p>

                    <div className="appform__role-detail-grid">
                      <div className="appform__role-detail-col">
                        <div className="appform__role-detail-h">Qué harás</div>
                        <ul className="appform__role-detail-list appform__role-detail-list--accent">
                          {sel.willDo.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="appform__role-detail-col">
                        <div className="appform__role-detail-h">Qué necesitas</div>
                        <ul className="appform__role-detail-list">
                          {sel.needs.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="appform__role-detail-footer">{sel.footer}</div>
                  </div>
                );
              })()}

              {formErrors.recruitmentRole && (
                <p style={{ color: 'var(--ik-danger)', fontSize: 12, marginTop: 6 }}>
                  {formErrors.recruitmentRole}
                </p>
              )}
            </div>

            <div className="ik-field">
              <label className="ik-label">
                Cuéntanos tu experiencia laboral y por qué quieres trabajar con nosotros
              </label>
              <textarea
                className="ik-input"
                value={form.aboutMe}
                onChange={(e) => updateField('aboutMe', e.target.value)}
                placeholder="Describe brevemente tu experiencia..."
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
              <button className="ik-btn ik-btn--primary" onClick={submitForm}>
                Continuar → Prueba diagnóstica
              </button>
            </div>
          </div>
        )}

        {/* ══ PASO 2: Diagnóstico ══ */}
        {step === 2 && (
          <div className="appform__section ik-fade-in-up">
            <h2 className="ik-section-title">Prueba diagnóstica</h2>
            {(() => {
              const currentRole = roles.find((r) => r.key === form.recruitmentRole);
              if (!currentRole) return null;
              return (
                <div className="appform__role-pill" aria-label={`Postulando a ${currentRole.label}`}>
                  <span className="appform__role-pill-icon" aria-hidden="true">{currentRole.icon}</span>
                  <span className="appform__role-pill-label">Postulando a</span>
                  <span className="appform__role-pill-value">{currentRole.label}</span>
                </div>
              );
            })()}
            <p className="ik-section-sub">
              {questions.length} preguntas sobre tu rol. Tiempo límite: {Math.ceil(timeLimit / 60)} minutos.
            </p>

            {!diagStarted ? (
              <>
                <div className="appform__intro-video">
                  <video
                    ref={introVideoRef}
                    className="appform__intro-video-el"
                    src="/intro.mp4"
                    autoPlay
                    playsInline
                    preload="auto"
                    onPlay={() => setIntroPlaying(true)}
                    onEnded={handleIntroEnded}
                    onContextMenu={(e) => e.preventDefault()}
                  />
                  {!introPlaying && !introWatched && (
                    <button
                      type="button"
                      className="appform__intro-play"
                      onClick={playIntro}
                      aria-label="Reproducir video"
                    >
                      <span className="appform__intro-play-icon" aria-hidden="true">
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </span>
                      Reproducir
                    </button>
                  )}
                  {introWatched && (
                    <div className="appform__intro-video-done">
                      <span aria-hidden="true">✓</span> Video visto
                    </div>
                  )}
                </div>
                <div className="ik-alert ik-alert--info" style={{ marginTop: 20 }}>
                  El tiempo empieza cuando presiones "Comenzar". No hay respuestas trampa, queremos ver lo que ya sabes.
                </div>
                <button
                  className="ik-btn ik-btn--primary"
                  onClick={startDiag}
                  disabled={!introWatched}
                >
                  Comenzar prueba →
                </button>
                {!introWatched && (
                  <p className="appform__intro-hint">
                    Mira el video completo para habilitar la prueba.
                  </p>
                )}
                {/* Botón para saltar el video. Aparece cuando SHOW_DEV_TOOLS=true
                    (local) o ALLOW_SKIP_VIDEO=true (staging Vercel). En producción
                    ninguna de las dos está seteada y el botón no se renderiza. */}
                {ALLOW_SKIP_VIDEO && !introWatched && (
                  <button
                    type="button"
                    className="appform__intro-skip-dev"
                    onClick={() => setIntroWatched(true)}
                  >
                    🛠 Saltar video (solo testing)
                  </button>
                )}
              </>
            ) : (
              <div className="appform__diag" style={{ marginTop: 20 }}>
                {/* Timer (oculto en QA mode) */}
                {!SHOW_DEV_TOOLS && (
                  <>
                    <div className="appform__timer-row">
                      <span className="appform__timer-label">Tiempo restante</span>
                      <span className={`appform__timer-value ${timerPercent < 15 ? 'appform__timer-value--danger' : ''}`}>
                        {formatTime(timeLeft)}
                      </span>
                    </div>
                    <div className="ik-timer-bar">
                      <div
                        className={`ik-timer-bar__fill ${timerClass}`}
                        style={{ width: `${timerPercent}%` }}
                      />
                    </div>
                  </>
                )}

                {/* Contador QA: comentadas / total */}
                {SHOW_DEV_TOOLS && (
                  <div className="appform__qa-progress">
                    <span className="appform__qa-progress-label">
                      🛠 Modo QA — sin timer
                    </span>
                    <span className="appform__qa-progress-count">
                      Comentadas: <strong>{questions.filter((qq) => qaComments[qq.id]).length}</strong> / {questions.length}
                    </span>
                  </div>
                )}

                {/* Question card */}
                {questions[currentQ] && (
                  <div className="appform__q-card ik-fade-in" key={currentQ}>
                    <div className="appform__q-num ik-eyebrow">
                      Pregunta {currentQ + 1} de {questions.length}
                    </div>
                    {questions[currentQ].context && (
                      <div className="appform__q-context">{questions[currentQ].context}</div>
                    )}
                    <div className="appform__q-text">{questions[currentQ].question}</div>
                    {questions[currentQ].image && (
                      <div className="appform__q-photo">
                        <img
                          src={questions[currentQ].image}
                          alt={`Situación: ${questions[currentQ].question}`}
                          loading="lazy"
                        />
                      </div>
                    )}
                    <div className="appform__q-options">
                      {questions[currentQ].options.map((opt, j) => {
                        const isCorrect = SHOW_DEV_TOOLS && j === questions[currentQ].correct;
                        return (
                          <button
                            key={j}
                            className={[
                              'appform__q-opt',
                              answers[currentQ] === j ? 'appform__q-opt--selected' : '',
                              isCorrect ? 'appform__q-opt--correct-dev' : '',
                            ].filter(Boolean).join(' ')}
                            onClick={() => selectAnswer(currentQ, j)}
                          >
                            <span className="appform__q-opt-letter">
                              {String.fromCharCode(65 + j)}
                            </span>
                            <span className="appform__q-opt-text">{opt}</span>
                            {isCorrect && (
                              <span className="appform__q-opt-correct-badge">✓ correcta</span>
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {SHOW_DEV_TOOLS && (
                      <div className="appform__qa-editor">
                        <div className="appform__qa-editor-head">
                          <span className="appform__qa-editor-title">
                            🛠 QA — comentar sobre esta pregunta
                          </span>
                          <span className="appform__qa-editor-id">
                            id: {questions[currentQ].id}
                          </span>
                        </div>
                        <div className="appform__qa-type-row">
                          <span className="appform__qa-type-label">Tipo de cambio:</span>
                          <div className="appform__qa-type-group" role="radiogroup">
                            {QA_CHANGE_TYPES.map((t) => (
                              <button
                                key={t.value}
                                type="button"
                                role="radio"
                                aria-checked={qaChangeType === t.value}
                                className={`appform__qa-type-btn ${qaChangeType === t.value ? `appform__qa-type-btn--active appform__qa-type-btn--${t.value}` : ''}`}
                                onClick={() => setQaChangeType(t.value)}
                              >
                                {t.emoji} {t.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <textarea
                          className="appform__qa-editor-textarea"
                          placeholder="¿Qué cambiarías de esta pregunta o foto? (texto confuso, foto borrosa, opciones mal redactadas, etc.)"
                          value={qaDraft}
                          onChange={(e) => setQaDraft(e.target.value)}
                          rows={3}
                        />
                        <div className="appform__qa-editor-actions">
                          <span className="appform__qa-editor-status">
                            {qaSaving
                              ? '💾 guardando...'
                              : qaComments[questions[currentQ].id]
                                ? '✓ auto-guardado'
                                : 'Auto-guardado mientras escribís'}
                          </span>
                          {qaComments[questions[currentQ].id] && (
                            <button
                              type="button"
                              className="appform__qa-btn appform__qa-btn--delete"
                              onClick={() => removeQaComment(questions[currentQ].id)}
                            >
                              Borrar
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Nav */}
                <div className="appform__q-nav">
                  <span style={{ fontSize: 13, color: 'var(--ik-gray-500)' }}>
                    {currentQ + 1} de {questions.length}
                    {SHOW_DEV_TOOLS && questions[currentQ] && qaComments[questions[currentQ].id] && (
                      <span className="appform__qa-already-pill"> ✓ ya comentada</span>
                    )}
                  </span>
                  {SHOW_DEV_TOOLS && currentQ > 0 && (
                    <button
                      className="ik-btn ik-btn--ghost"
                      onClick={() => setCurrentQ((p) => Math.max(0, p - 1))}
                      style={{ marginRight: 8 }}
                    >
                      ← Anterior
                    </button>
                  )}
                  <button
                    className="ik-btn ik-btn--primary"
                    disabled={!SHOW_DEV_TOOLS && answers[currentQ] === undefined}
                    onClick={nextQuestion}
                  >
                    {currentQ === questions.length - 1 ? 'Finalizar →' : 'Siguiente →'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ PASO 3: Enviado ══ */}
        {step === 3 && (
          <div className="appform__section appform__done ik-fade-in-up">
            <div className="appform__done-icon">✅</div>
            <h2 className="ik-section-title" style={{ textAlign: 'center' }}>
              ¡Postulación recibida!
            </h2>

            <p className="ik-section-sub" style={{ textAlign: 'center', marginTop: 12, maxWidth: 440, margin: '12px auto 0' }}>
              Revisaremos tu postulación y en caso de quedar seleccionado/a recibirás un mensaje por WhatsApp
              con acceso a la Academia Impekble.
            </p>

            <div className="ik-alert ik-alert--warning" style={{ marginTop: 28 }}>
              📱 <strong>Mantén tu WhatsApp activo.</strong> El acceso a la academia llegará en las próximas 24–48 horas si quedas seleccionado/a.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
